import type { PartnerHotelCurationItemSource } from "@/types/partner-destination";

export type CurationVisibilityFilter = "all" | "visible" | "hidden";

export type CurationSourceFilter = "curated" | "hotel";

export type CurationFiltersState = {
  visibility: CurationVisibilityFilter;
  /** Empty = any source. */
  sources: CurationSourceFilter[];
  minPrice: number | null;
  maxPrice: number | null;
};

export const DEFAULT_CURATION_FILTERS: CurationFiltersState = {
  visibility: "all",
  sources: [],
  minPrice: null,
  maxPrice: null,
};

export const CURATION_SOURCE_OPTIONS: CurationSourceFilter[] = [
  "curated",
  "hotel",
];

export function curationSourceOf(
  source: string | null | undefined,
): CurationSourceFilter | null {
  switch (source) {
    case "ai_top":
    case "ai_score":
    case "manual":
      return "curated";
    case "hotel":
      return "hotel";
    default:
      return null;
  }
}

export type CurationFilterableItem = {
  productCode: string;
  hidden: boolean;
  source: PartnerHotelCurationItemSource | null;
  price: number | null;
};

export function countActiveCurationFilters(
  filters: CurationFiltersState,
): number {
  let count = 0;
  if (filters.visibility !== "all") count += 1;
  if (filters.sources.length > 0) count += 1;
  if (filters.minPrice !== null || filters.maxPrice !== null) count += 1;
  return count;
}

function matchesSourceAndPrice(
  item: CurationFilterableItem,
  filters: CurationFiltersState,
): boolean {
  if (filters.sources.length > 0) {
    const source = curationSourceOf(item.source);
    if (source === null || !filters.sources.includes(source)) return false;
  }
  if (filters.minPrice !== null || filters.maxPrice !== null) {
    if (item.price === null) return false;
    if (filters.minPrice !== null && item.price < filters.minPrice) {
      return false;
    }
    if (filters.maxPrice !== null && item.price > filters.maxPrice) {
      return false;
    }
  }
  return true;
}

/** City list: visibility, source and price. Returns the same item objects. */
export function filterCityItems<T extends CurationFilterableItem>(
  items: readonly T[],
  filters: CurationFiltersState,
): T[] {
  return items.filter((item) => {
    if (filters.visibility === "visible" && item.hidden) return false;
    if (filters.visibility === "hidden" && !item.hidden) return false;
    return matchesSourceAndPrice(item, filters);
  });
}

/** "Added by this hotel": source and price only (added items have no hidden state). */
export function filterAddedItems<T extends CurationFilterableItem>(
  items: readonly T[],
  filters: CurationFiltersState,
): T[] {
  return items.filter((item) => matchesSourceAndPrice(item, filters));
}
